import { z } from 'zod';
import { localeSchema, timestamp, uuid } from './common.js';
/**
 * Identity & auth entities (§4). `users`, `sessions`, `oauth_identities`,
 * `magic_link_tokens`.
 */
/** Platform RBAC (F68) — orthogonal to `tier`. `isStaff ≡ role IN (moderator,admin)`. */
export const userRole = z.enum(['user', 'moderator', 'admin']);
/** Billing state (F20) — orthogonal to `role`. */
export const userTier = z.enum(['free', 'pro']);
/** Account lifecycle. `suspended` blocks login/actions (F54). */
export const userStatus = z.enum(['active', 'suspended', 'deleted']);
/** App UI theme preference (E4). Distinct from document Themes and reader theme. */
export const themePref = z.enum(['system', 'light', 'dark']);
export const userSchema = z.object({
    id: uuid,
    email: z.string().email(),
    emailVerifiedAt: timestamp.nullable(),
    displayName: z.string().min(1).max(120).nullable(),
    /**
     * F10 profile avatar (§27). Plain `uuid` with NO FK constraint — mirrors
     * `credit_ledger.refId`'s precedent (`packages/db/src/schema/money.ts`):
     * `identity.ts` loads before `files.ts` in the FK dependency order
     * (`packages/db/src/schema/index.ts`), so a real FK here would create a
     * circular import. Referential integrity is enforced in
     * `settings.updateProfile`'s handler (`@xdocs/core`). Null = default
     * mascot avatar (F10 AC).
     */
    avatarFileId: uuid.nullable(),
    /** argon2id hash; null for OAuth-only / magic-link-only accounts. Never leaves the server. */
    passwordHash: z.string().nullable(),
    role: userRole,
    tier: userTier,
    status: userStatus,
    locale: localeSchema,
    theme: themePref,
    /** Materialized SUM(credit_ledger.delta). Live balance authority — NEVER cached. */
    creditsBalance: z.number().int(),
    /** FTUE mascot tutorial completion flag (F9). */
    ftueCompletedAt: timestamp.nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
    deletedAt: timestamp.nullable(),
});
/**
 * A safe projection of `users` for the socket/UI — no `passwordHash`, no PII
 * beyond what the owner sees about themselves.
 */
export const userPublicSchema = userSchema.omit({ passwordHash: true });
export const sessionSchema = z.object({
    id: uuid,
    userId: uuid,
    /** SHA-256 of the refresh token; raw token only ever in the cookie/transit. */
    refreshTokenHash: z.string(),
    userAgent: z.string().nullable(),
    ip: z.string().nullable(),
    createdAt: timestamp,
    lastSeenAt: timestamp,
    expiresAt: timestamp,
    /** Set when revoked (logout / force-logout F54 / rotation). */
    revokedAt: timestamp.nullable(),
});
/** Session list row surfaced to the owner (F13) — no token material. */
export const sessionPublicSchema = sessionSchema.omit({ refreshTokenHash: true });
export const oauthProvider = z.enum(['google']);
export const oauthIdentitySchema = z.object({
    id: uuid,
    userId: uuid,
    provider: oauthProvider,
    /** Provider's stable subject id (`sub`). Unique per (provider, providerUserId). */
    providerUserId: z.string().min(1),
    createdAt: timestamp,
});
/** Disambiguates a one-time token's use (F42/F50/magic-link sign-in). */
export const magicLinkPurpose = z.enum(['magic_link', 'password_reset', 'email_verify']);
export const magicLinkTokenSchema = z.object({
    id: uuid,
    userId: uuid,
    purpose: magicLinkPurpose,
    /** SHA-256 of the token; the raw token exists only in the emailed URL. */
    tokenHash: z.string(),
    expiresAt: timestamp,
    consumedAt: timestamp.nullable(),
    createdAt: timestamp,
});
